/* eslint-disable react/no-unknown-property */
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import parse from 'html-react-parser';
import usePagePath from 'hooks/usePagePath';
import favicon from 'images/favicon.ico';
import config from 'utils/siteConfig';
import { updatePageLoaded, updateAudience } from 'services/dataLayerService';
import { generateFAQSchema, getRobot, generateBreadcrumbSchema } from 'utils/seo';

const SEO = props => {
  const {
    pageSEO,
    postSEO,
    noIndexOption,
    pageNode,
    pagePath,
    canonicalUrl,
    productSchema,
    ogTagImage,
    slug,
    pageCategory,
    faq,
    audience,
    breadcrumb,
    headHtml,
    ...otherProps
  } = props;
  let title;
  let description;
  let websitePageSchema;
  let faqSchema;
  let breadcrumbSchema;
  title = config.siteTitle;
  description = config.siteDescription;

  if (typeof window === 'object' && window.intellimize) {
    intellimize.push(['activate']);
  }

  useEffect(() => {
    if (pageCategory) {
      updatePageLoaded(pageCategory);
    }
    const audienceData = audience === 'Both' ? 'Customer, Prospect' : audience;
    updateAudience(audienceData);
  }, [pageCategory, audience]);

  // If Normal Page
  if (pageNode) {
    title = pageNode.title;
    description = pageNode.metaDescription.internal.content;
  }

  // Default Website Schema
  const websiteSchema = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: config.siteUrl,
    name: title,
    alternateName: config.siteTitleAlt ? config.siteTitleAlt : '',
  };

  // Page SEO Schema
  if (pageSEO) {
    websitePageSchema = {
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      url: pagePath,
      name: title,
    };
  }

  // FAQ SEO Schema
  if (faq) {
    faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq,
    };
  }

  if (breadcrumb) {
    breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumb,
    };
  }

  const robot = getRobot(noIndexOption, slug, otherProps.isThankYouPage);
  const optimizeContainerId = 'OPT-T6HTLQG';

  return (
    <Helmet>
      <script src="//get.servicetitan.com/js/forms2/js/forms2.min.js" async="true" />
      <script src={`https://www.googleoptimize.com/optimize.js?id=${optimizeContainerId}`} />
      <script src="https://api.intellimize.co/client/117774277.js" />
      <script
        async
        src="https://ob.cheqzone.com/clicktrue_invocation.js?id=11605"
        data-ch="cheq4ppc"
        class="ct_clicktrue_11605"
      />
      <link rel="icon" href={favicon} />
      {/* General tags */}
      {canonicalUrl && noIndexOption && <link rel="canonical" href={canonicalUrl} />}
      <title>{title}</title>
      {/* <meta name="image" content={image} /> */}
      <meta name="description" content={description} />
      {robot}
      {/* Schema.org tags */}
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      {websitePageSchema && (
        <script type="application/ld+json">{JSON.stringify(websitePageSchema)}</script>
      )}
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
      {productSchema && <script type="application/ld+json">{JSON.stringify(productSchema)}</script>}
      {/* Mutiny */}
      <script>
        {`(function(){var a=window.mutiny=window.mutiny||{};if(!window.mutiny.client){a.client={_queue:{}};var b=["identify","trackConversion"];var c=[].concat(b,["defaultOptOut","optOut","optIn"]);var d=function factory(c){return function(){for(var d=arguments.length,e=new Array(d),f=0;f<d;f++){e[f]=arguments[f]}a.client._queue[c]=a.client._queue[c]||[];if(b.includes(c)){return new Promise(function(b,d){a.client._queue[c].push({args:e,resolve:b,reject:d});setTimeout(d,500)})}else{a.client._queue[c].push({args:e})}}};c.forEach(function(b){a.client[b]=d(b)})}})();`}
      </script>
      <script src="https://client-registry.mutinycdn.com/personalize/client/f649ed61a3088943.js"></script>

      {/* OpenGraph tags */}
      <meta property="og:title" content={title} />
      {postSEO ? <meta property="og:type" content="article" /> : null}
      <meta
        property="og:image"
        content={
          ogTagImage
            ? `https:${ogTagImage}`
            : 'https://images.ctfassets.net/3q1a0w8cwuwf/7ISRCzbAIAcs4MGNvrYSWN/67264761ca3557c758165afc41dc04a9/ServiceTitan_Logo_Black_2.png'
        }
      />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={config.siteTitle} />
      <meta property="og:url" content={pagePath} />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1,user-scalable=0"
      />
      {/* Twitter Card tags */}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {headHtml && parse(headHtml)}
      <html lang="en" />
    </Helmet>
  );
};

export default SEO;

export const createSEO = (contentfulSeo, props, contentfulFaq = {}, breadcrumbs = {}) => {
  const metaDescription = contentfulSeo?.metaDescription?.metaDescription;
  const pageUrl = contentfulSeo?.pageUrl || contentfulSeo?.slug;
  const pageTitle = contentfulSeo?.pageTitle || contentfulSeo?.titleTag || contentfulSeo?.title;
  const indexibility = contentfulSeo?.indexibility?.indexibility || contentfulSeo?.indexable;
  const featuredImage =
    contentfulSeo?.featuredImage?.image?.file?.url ||
    contentfulSeo?.socialSharingImage?.image?.file?.url;
  const canonicalUrl = contentfulSeo?.canonicalUrl;
  const pageCategory = contentfulSeo?.pageCategory || contentfulSeo?.pageCategories;
  const pagePath = usePagePath(props);
  const faq = generateFAQSchema(contentfulFaq?.components);
  const audience = contentfulSeo?.audience;
  const breadcrumb = generateBreadcrumbSchema(breadcrumbs?.data);
  const headHtml = contentfulSeo?.headHtml?.headHtml;

  const seo = {
    pageSEO: true,
    noIndexOption:
      indexibility === 'Non-Indexable' ? false : indexibility === 'Indexable' ? true : null,
    pageNode: {
      title: pageTitle,
      metaDescription: {
        internal: {
          content: metaDescription,
        },
      },
    },
    ogTagImage: featuredImage,
    pagePath: pagePath || pageUrl,
    canonicalUrl: canonicalUrl || pagePath || `https://www.servicetitan.com/${pageUrl}`,
    productSchema: ['Industry', 'Feature', 'Demo', 'Tools', 'Comparison'].includes(pageCategory)
      ? {
          '@context': 'http://schema.org',
          '@type': 'Product',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.5',
            reviewCount: '679',
          },
          name: 'ServiceTitan',
        }
      : null,
    slug: pageUrl,
    pageCategory,
    faq,
    audience,
    breadcrumb,
    headHtml,
  };

  return seo;
};
